import { motion, AnimatePresence } from 'motion/react';
import { Heart, X } from 'lucide-react';

interface ResultModalProps {
  isOpen: boolean;
  result: string;
  hearts: number;
  onClose: () => void;
}

export function ResultModal({ isOpen, result, hearts, onClose }: ResultModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
          />
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="w-[320px] max-w-[calc(100vw-2rem)] pointer-events-auto">
              <div className="bg-white rounded-3xl p-8 shadow-2xl relative">
                {/* Close button */}
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>

                {/* Confetti decoration */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="text-6xl"
                  >
                    🎉
                  </motion.div>
                </div>

                {/* Content */}
                <div className="text-center mt-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: 0.3,
                      type: 'spring',
                      bounce: 0.5
                    }}
                    className="inline-block mb-4"
                  >
                    <div className="bg-gradient-to-br from-pink-100 to-blue-100 rounded-2xl px-[18px] py-[12px]">
                      <p className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent">
                        {result === 'Do'
                          ? '도'
                          : result === 'Gae'
                            ? '개'
                            : result === 'Geol'
                              ? '걸'
                              : result === 'Yut'
                                ? '윷'
                                : result === 'Mo'
                                  ? '모'
                                  : result}
                      </p>
                    </div>
                  </motion.div>

                  {/* Hearts display */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: 0.4,
                      type: 'spring',
                      bounce: 0.6
                    }}
                    className="flex items-center justify-center gap-2 mx-[0px] mt-[0px] mb-[8px]"
                  >
                    <div className="flex items-center gap-1">
                      {Array.from({
                        length: Math.min(hearts, 5)
                      }).map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{
                            delay: 0.5 + i * 0.1,
                            type: 'spring'
                          }}
                        >
                          <Heart size={32} className="fill-red-500 text-red-500" />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  <h3 className="font-semibold mx-[0px] mt-[0px] mb-[20px] text-[18px] text-[#505050]">
                    하트 {hearts}개 받았어요 💕
                  </h3>

                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full bg-gradient-to-r from-pink-500 to-blue-500 text-white py-3 rounded-full font-semibold hover:shadow-lg transition-shadow"
                  >
                    확인
                  </button>

                  <p className="mt-4 text-[14px] text-[#7e7e7e]">
                    올해도 FanHearts에서 즐겁게 놀아요!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
